import React, { useCallback, useEffect } from "react";
import { darken } from "polished";
import cns from "classnames";
import { Observer } from "mobx-react";
import { Paper, withStyles } from "@material-ui/core";
import { AutoSizer, defaultTableRowRenderer, TableRowRenderer } from "react-virtualized";

import { Classes, ThemeType } from "@theme";
import SearchPageLayout from "@search/components/SearchPageLayout";
import SearchResultLayout from "@search/components/SearchResultLayout";
import SearchOptions from "@search/SearchOptions";
import TableSearch from "@search/TableSearch/TableSearch";
import ColumnSizer from "@search/TableSearch/ColumnSizer";
import TableSubheader from "@search/TableSearch/TableSubheader";
import TableSearchHeader from "@search/TableSearch/TableSearchHeader";

import { BookingFinanceSearchStore } from "./BookingFinanceSearchStore";
import { getColumns } from "./BookingFinanceSearchColumns";
import { BookingSearchForm } from "../BookingSearchForm";
import { BookingFragment, RowType } from "./utils";
import { BookingFilterGroup, groupBy } from "../utils";
import { getPollLimit } from "../poll-limit";
import { BookingSearchActionBar } from "../BookingSearchActionBar";
import { usePolling } from "@bonliva-crm/ui";
import { BookingStatusType } from "@graphql";
import { BookingProfileLocation } from "@navigation";
import { rowRendererLink } from "@common/renderers";

type Props = {
  onOpen: (id: string, tab?: string, scrollContainer?: HTMLElement) => void;
  onEdit: (id?: string) => void;
  onGroupBy: (groupBy: BookingFilterGroup) => void;
  classes: Classes<typeof styles>;
};

const BookingFinanceSearch: React.FC<Props> = ({
  classes,
  onOpen,
  onEdit,
  onGroupBy
}) => {
  const store = React.useMemo(() => new BookingFinanceSearchStore(), []);

  useEffect(() => {
    return () => {
      store.unsubscribe();
    };
  }, [store]);

  usePolling(
    async () => {
      await store.queryFuncWatch.refetch({
        filter: store.filterManager.queryInput,
        limit: getPollLimit(store.searchManager.items?.length),
        skip: 0
      });
    },
    "/bookings",
    { interval: 5000, refetchOnPushState: true }
  );

  const columns = React.useMemo(() => getColumns(store), [store]);

  const renderFilter = useCallback(() => {
    return (
      <div>
        <BookingSearchForm
          manager={store.filterManager}
          group={BookingFilterGroup.Finance}
        />
      </div>
    );
  }, [store.filterManager]);

  const renderGroupHeadline = useCallback(() => {
    const { searchManager } = store;

    return (
      <Observer>
        {() => {
          if (!searchManager.header) {
            return null;
          }

          return (
            <TableSubheader
              center
              mainText={searchManager.header.week}
              smallText={searchManager.header.year}
            />
          );
        }}
      </Observer>
    );
  }, [store.searchManager]);

  const rowRenderer: TableRowRenderer = useCallback(
    (rowProps) => {
      const row: RowType = rowProps.rowData;

      if (row.type === "group") {
        return (
          <div
            className={cns(rowProps.className, classes.subHeader)}
            aria-rowindex={rowProps.index}
            key={row._id}
            role="row"
            style={{
              ...rowProps.style,
              width: rowProps.style.width - 1
            }}
          >
            <TableSubheader
              center
              mainText={row.header.week}
              smallText={row.header.year}
            />
          </div>
        );
      }

      return defaultTableRowRenderer({
        ...rowProps,
        rowData: row.data
      });
    },
    [classes.subHeader]
  );

  const getRowClassName = useCallback(
    (opts: { index: number }) => {
      const item = store.searchManager.getRow(opts.index);

      if (!item) {
        return "";
      }

      if (item.type !== "card") {
        return;
      }

      const isOdd = opts.index % 2 !== 1;

      return cns(classes.row, {
        [classes.rowOk]: item.data.bookingStatus.status === BookingStatusType.Closed,
        [classes.rowInProgress]:
          item.data.bookingStatus.status === BookingStatusType.Billed ||
          item.data.bookingStatus.status === BookingStatusType.Paid,
        [classes.rowCanceled]:
          item.data.bookingStatus.status === BookingStatusType.Canceled,
        odd: isOdd
      });
    },
    [
      store.searchManager,
      classes.row,
      classes.rowOk,
      classes.rowInProgress,
      classes.rowCanceled
    ]
  );

  return (
    <SearchPageLayout
      delayed
      wrapContent={(content) => <Paper>{content}</Paper>}
      actionBar={
        <Observer>
          {() => (
            <>
              <BookingSearchActionBar
                onDirectBooking={() => onEdit(null)}
                total={store.total}
                withTotal
              />

              <SearchOptions
                activeGroupBy={BookingFilterGroup.Finance}
                groupBy={groupBy()}
                onGroupBy={(v) => onGroupBy(v.value)}
                onExcel={(type) => store.getCSV(type)}
                onExcelBg={(type) => store.getCSVBg(type)}
                isExcelLoading={store.isDownloading}
                dateRange={{
                  from: store.filterManager.filter.from,
                  to: store.filterManager.filter.to
                }}
              />
            </>
          )}
        </Observer>
      }
      content={(scrollContainer) => (
        <SearchResultLayout fittedContent fitted style={{ maxWidth: 9999 }}>
          <AutoSizer disableHeight className={classes.stickyHeader}>
            {({ width }) => (
              <div style={{ background: "white", position: "relative", width }}>
                <ColumnSizer columns={columns} width={width}>
                  {({ ref }) => (
                    <TableSearchHeader
                      columns={columns}
                      columnSizer={ref}
                      style={{ backgroundColor: "#fff" }}
                    />
                  )}
                </ColumnSizer>
                <div style={{ width }}>{renderGroupHeadline()}</div>
              </div>
            )}
          </AutoSizer>

          <div>
            <TableSearch
              disableHeader
              dynamicColIndex={2}
              scrollContainer={scrollContainer}
              manager={store.searchManager}
              columns={columns}
              rowClassName={getRowClassName}
              rowHeight={({ index }) => {
                const row = store.searchManager.getRow(index);
                return row.type === "group" ? 48 : 80;
              }}
              rowRenderer={rowRendererLink(
                rowRenderer,
                (row: { rowData?: { data: BookingFragment } }) => {
                  const bookingId = row.rowData?.data?.BookingRef?.NodeID;

                  if (!bookingId) {
                    return;
                  }

                  return {
                    to: new BookingProfileLocation(bookingId).toURI(),
                    onClick: (e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      onOpen(bookingId, "finance", scrollContainer);
                    }
                  };
                }
              )}
              rowStyle={{ cursor: "pointer" }}
              onRowsRendered={store.searchManager.onRowsRendered}
            />
          </div>
        </SearchResultLayout>
      )}
      filter={renderFilter()}
    />
  );
};

const darker = 0.02;

const bg = (color) => ({
  backgroundColor: color,
  "&.odd": { backgroundColor: darken(darker, color) },
  "&:hover": { backgroundColor: darken(darker * 5, color) },
  "&.odd:hover": { backgroundColor: darken(darker * 5, color) }
});

const styles = (theme: ThemeType) => ({
  subHeader: { display: "flex", height: 48, pointerEvents: "none" },
  stickyHeader: { position: "sticky", top: 0, zIndex: 1 },
  shadow: {
    position: "absolute",
    bottom: 0,
    top: 10,
    left: 4,
    right: 4,
    zIndex: -1,
    boxShadow: "0 0 4px gray"
  },

  row: {
    "& *": {
      fontSize: 14
    }
  },

  rowOk: bg("#D5E6C8"),
  rowInProgress: bg("#F9F0DA"),
  rowCanceled: bg("#FFD4D4")
});

export default withStyles(styles as any)(BookingFinanceSearch);
